import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    AreaChart, Area, PieChart, Pie,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    Cell,BarChart, Bar,LabelList
} from 'recharts';
import { format, subMonths, parseISO, differenceInMonths} from 'date-fns';
import Loader from '../../loader/Loader';
import styles from './Dashboard.module.css';
import { getCategories } from '../../services/categoryService';
import { getDepartments } from '../../services/departmentService';
import { getWarehouses } from '../../services/warehouseService';
import EditTrackedProductsModal from '../../components/EditTrackedProductsModal/EditTrackedProductsModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d'];
const BAR_BASE_HUE = 260;          // start near purple
const BAR_HUE_STEP = 25;           // each bar = +25° hue


const Dashboard = () => {
    const [trackedProductIds, setTrackedProductIds] = useState([]);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [allProducts, setAllProducts] = useState([]); // To pass to the modal
    const [statistics, setStatistics] = useState({
        orders: {
            total: 0,
            committed: 0,
            issued: 0,
            returned: 0,
            partially_returned: 0,
            cancelled: 0,
            averageValue: 0,
            cancellationRate: 0,
            partialReturnRate: 0,
            returnRate: 0
        },
        products: {
            total: 0,
            availableProducts: 0,
            totalItems: 0, 
            availableItems: 0,    
            warehouseBreakdown: {},
            categoryBreakdown: {},
            departmentBreakdown: {},
            topProducts: [] ,
            trackedProductsData: [] 
        },
        users: {
            total: 0,
            active: 0,
            adminUsers: 0,
            regularUsers: 0
        },
        orderTrends: []
    });

    const [orderOverviewTimeRange, setOrderOverviewTimeRange] = useState('6');
    const [orderTrendsTimeRange, setOrderTrendsTimeRange] = useState('6');
    const [isLoading, setIsLoading] = useState(true);
    const [distributionViews, setDistributionViews] = useState({
        warehouse: 'quantity',
        category: 'quantity',
        department: 'quantity'
    });

    const calculateOrderTrends = useCallback((orders, monthsBack) => {
        if (monthsBack === 'all') {
            // For "All Time", get the date of the earliest order
            const earliestDate = new Date(Math.min(...orders.map(o => new Date(o.fromDate))));
            monthsBack = differenceInMonths(new Date(), earliestDate) + 1;
        }
        const trends = [];
        const endDate = new Date();
        const startDate = subMonths(endDate, monthsBack);
    
        for (let i = 0; i < monthsBack; i++) {
            const date = subMonths(endDate, i);
            trends.unshift({
                month: format(date, 'MMM yy'),
                orders: 0
            });
        }
    
        orders.forEach(order => {
            const orderDate = new Date(order.fromDate);
            if (orderDate >= startDate) {
                const monthIndex = trends.findIndex(
                    t => t.month === format(orderDate, 'MMM yy')
                );
                if (monthIndex !== -1) {
                    trends[monthIndex].orders++;
                }
            }
        });
    
        return trends;
    }, []);

// Dashboard.js

const fetchStatistics = useCallback(async () => {
    setIsLoading(true);
    try {
        const [ordersRes, productsRes, categoriesRes, warehousesRes, departmentsRes, usersRes,topProductsRes] =
            await Promise.all([
                axios.get(`${BACKEND_URL}/api/orders/getorders`),
                axios.get(`${BACKEND_URL}/api/products/getproducts`),
                getCategories(),
                getWarehouses(),
                getDepartments(),
                axios.get(`${BACKEND_URL}/api/users/getusers`),
                axios.get(`${BACKEND_URL}/api/top-products`) 

            ]);
        
        // ✨ Store all products for the modal
        setAllProducts(productsRes.data);

        // --- Step 1: Calculate ALL product-related stats that don't depend on each other ---
        
        // Calculate product distribution first
        const productStats = {
            total: productsRes.data.length,
            availableProducts: productsRes.data.filter(p => p.quantityAvailable > 0).length,
            totalItems: productsRes.data.reduce((sum, p) => sum + p.quantity, 0),
            availableItems: productsRes.data.reduce((sum, p) => sum + p.quantityAvailable, 0),
            warehouseBreakdown: { quantity: {}, products: {} },
            categoryBreakdown: { quantity: {}, products: {} },
            departmentBreakdown: { quantity: {}, products: {} },
            topProducts: [],
            trackedProductsData: []
        };

        productsRes.data.forEach(product => {
            const warehouse = warehousesRes.find(w => w._id === product.warehouse)?.name || 'Unknown';
            const category = categoriesRes.find(c => c._id === product.category)?.name || 'Unknown';
            const department = departmentsRes.find(d => d._id === product.department)?.name || 'Unknown';
            productStats.warehouseBreakdown.quantity[warehouse] = (productStats.warehouseBreakdown.quantity[warehouse] || 0) + product.quantityAvailable;
            productStats.categoryBreakdown.quantity[category] = (productStats.categoryBreakdown.quantity[category] || 0) + product.quantityAvailable;
            productStats.departmentBreakdown.quantity[department] = (productStats.departmentBreakdown.quantity[department] || 0) + product.quantityAvailable;
            productStats.warehouseBreakdown.products[warehouse] = (productStats.warehouseBreakdown.products[warehouse] || 0) + 1;
            productStats.categoryBreakdown.products[category] = (productStats.categoryBreakdown.products[category] || 0) + 1;
            productStats.departmentBreakdown.products[department] = (productStats.departmentBreakdown.products[department] || 0) + 1;
        });

        // Calculate product popularity from orders
        const productPopularity = {};
        ordersRes.data.forEach(order => {
            order.products.forEach(orderProduct => {
                const productId = orderProduct.product._id || orderProduct.product;
                const productData = productsRes.data.find(p => p._id === productId);
                if (productData) {
                    if (!productPopularity[productId]) {
                        productPopularity[productId] = { name: productData.productName, count: 0 };
                    }
                    productPopularity[productId].count += 1;
                }
            });
        });

        const popularityArray = Object.values(productPopularity).sort((a, b) => b.count - a.count);
        productStats.topProducts = popularityArray.slice(0, 5).map(p => ({ name: p.name, value: p.count }));

        // --- Step 2: Calculate tracked product data, now that popularity is known ---
        let idsToUseForTracking = trackedProductIds;
        if (trackedProductIds.length === 0) {
            if (topProductsRes.data && topProductsRes.data.length > 0) {
                // If top-products API returns data, use those IDs
                idsToUseForTracking = topProductsRes.data.map(p => p._id);
            } else {
                // Fallback: calculate popularity and use top 5 if no tracked products from API
                if (popularityArray.length > 0) {
                    idsToUseForTracking = popularityArray.slice(0, 5).map(p => {
                        const productEntry = Object.entries(productPopularity).find(([id, data]) => data.name === p.name);
                        return productEntry ? productEntry[0] : null;
                    }).filter(Boolean);
                }
            }
            // Update the state only if it was empty and we found new IDs
            if (idsToUseForTracking.length > 0) {
                setTrackedProductIds(idsToUseForTracking);
            }
        }

        productStats.trackedProductsData = idsToUseForTracking.map(productId => {
            const product = productsRes.data.find(p => p._id === productId);
            if (!product) return null;

            const usedQuantity = ordersRes.data
                .filter(o => o.orderStatus === 'committed' || o.orderStatus === 'issued')
                .flatMap(o => o.products)
                .filter(p => (p.product?._id || p.product) === productId)
                .reduce((sum, p) => sum + p.quantity, 0);

            return { name: product.productName, quantity: product.quantity, available: product.quantityAvailable, used: usedQuantity };
        }).filter(Boolean);

        // --- Step 3: Process remaining stats (orders, users) ---
        const overviewMonthsBack = parseInt(orderOverviewTimeRange);
        const overviewStartDate = orderOverviewTimeRange === 'all' ? new Date(0) : subMonths(new Date(), overviewMonthsBack);
        const filteredOrders = ordersRes.data.filter(order => new Date(order.fromDate) >= overviewStartDate);

        const totalValue = filteredOrders.reduce((sum, order) => sum + order.products.reduce((orderSum, p) => orderSum + (p.price * p.quantity), 0), 0);
        const orderStats = {
            total: filteredOrders.length,
            committed: filteredOrders.filter(o => o.orderStatus === 'committed').length,
            issued: filteredOrders.filter(o => o.orderStatus === 'issued').length,
            returned: filteredOrders.filter(o => o.orderStatus === 'returned').length,
            partially_returned: filteredOrders.filter(o => o.orderStatus === 'partially_returned').length,
            cancelled: filteredOrders.filter(o => o.orderStatus === 'cancelled').length,
            averageValue: filteredOrders.length > 0 ? totalValue / filteredOrders.length : 0,
            cancellationRate: filteredOrders.length > 0 ? (filteredOrders.filter(o => o.orderStatus === 'cancelled').length / filteredOrders.length) * 100 : 0,
            partialReturnRate: filteredOrders.length > 0 ? (filteredOrders.filter(o => o.orderStatus === 'partially_returned').length / filteredOrders.length) * 100 : 0,
            returnRate: filteredOrders.length > 0 ? (filteredOrders.filter(o => o.orderStatus === 'returned').length / filteredOrders.length) * 100 : 0,
        };

        const userStats = {
            total: usersRes.data.length,
            active: usersRes.data.filter(u => u.lastActivity && new Date(u.lastActivity) > new Date(Date.now() - 2 * 60 * 1000)).length,
            adminUsers: usersRes.data.filter(u => u.permission === 'admin').length,
            regularUsers: usersRes.data.filter(u => u.permission === 'user').length,
        };

        // --- Step 4: Set all state at once ---
        setStatistics({
            orders: orderStats,
            products: productStats,
            users: userStats,
            orderTrends: calculateOrderTrends(ordersRes.data, orderTrendsTimeRange),
        });

    } catch (error) {
        console.error('Error fetching statistics:', error);
    } finally {
        setIsLoading(false);
    }
}, [orderOverviewTimeRange, orderTrendsTimeRange, calculateOrderTrends, trackedProductIds]);

    const renderTrackedProductsChart = (data, title) => {
        // As per your drawing, pink and green colors
        const AVAILABLE_COLOR = '#FF80B3'; // A nice pink
        const USED_COLOR = '#82ca9d';      // Recharts default green

        return (
            <div className={styles.chartCard}>
                <div className={styles.chartHeader}>
                    <h3>{title}</h3>
                    {/* The "Edit" button from your drawing */}
                    <button
                        className={styles.editButton} // We'll add a style for this
                        onClick={() => setIsEditModalOpen(true)}
                    >
                        Edit
                    </button>
                </div>
                {data.length === 0 ? (
                    <div className={styles.noData}>Click 'Edit' to select products to monitor.</div>
                ) : (
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="quantity" fill={AVAILABLE_COLOR} name="Total Quantity" />
                            <Bar dataKey="used" fill={USED_COLOR} name="Items in Use" />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>
        );
    };


    // Corrected calculateOrderTrends to handle 'all' timeRangeString properly
    const calculateOrderTrendsCorrected = useCallback((orders, timeRangeString) => {
        let numMonthsToDisplay;
        const allOrders = orders || [];
    
        if (timeRangeString === 'all') {
            if (allOrders.length === 0) {
                numMonthsToDisplay = 1; 
            } else {
                const earliestOrderDate = new Date(Math.min(...allOrders.map(o => new Date(o.fromDate))));
                if (isNaN(earliestOrderDate.getTime())) {
                     numMonthsToDisplay = 1;
                } else {
                    numMonthsToDisplay = differenceInMonths(new Date(), earliestOrderDate) + 1;
                    if (numMonthsToDisplay <= 0) numMonthsToDisplay = 1;
                }
            }
        } else {
            numMonthsToDisplay = parseInt(timeRangeString, 10);
            if (isNaN(numMonthsToDisplay) || numMonthsToDisplay <= 0) {
                numMonthsToDisplay = 6; 
            }
        }
    
        const trends = [];
        const endDate = new Date();
        const startDateForFiltering = subMonths(endDate, numMonthsToDisplay);
    
        for (let i = 0; i < numMonthsToDisplay; i++) {
            const monthDate = subMonths(endDate, i);
            trends.unshift({ month: format(monthDate, 'MMM yy'), orders: 0 });
        }
        
        allOrders.forEach(order => {
            const orderDate = parseISO(order.fromDate);
            if (orderDate >= startDateForFiltering && orderDate <= endDate) {
                const monthKey = format(orderDate, 'MMM yy');
                const monthIndex = trends.findIndex(t => t.month === monthKey);
                if (monthIndex !== -1) trends[monthIndex].orders++;
            }
        });
        if (trends.length === 0) trends.push({ month: format(new Date(), 'MMM yy'), orders: 0 });
        return trends;
    }, []);

    useEffect(() => {
        fetchStatistics();
    }, [fetchStatistics]);

        const handleSaveTopProducts = useCallback(async (selectedProductIds) => {
        try {
            // Make the PUT request to update the top products in MongoDB
            await axios.put(`${BACKEND_URL}/api/top-products`, { productIds: selectedProductIds });
            console.log('Top products updated successfully!');
            // Update local state and re-fetch all statistics to reflect changes
            setTrackedProductIds(selectedProductIds);
            await fetchStatistics(); 
        } catch (error) {
            console.error('Error updating top products:', error);
            if (error.response && error.response.data && error.response.data.message) {
                alert(`Failed to update top products: ${error.response.data.message}`);
            } else {
                alert('Failed to update top products. Please try again.');
            }
        } finally {
            setIsEditModalOpen(false); // Always close the modal after an attempt to save
        }
    }, [fetchStatistics]);

    if (isLoading) return <Loader />;

const renderPieChart = (data, title, type) => {
    // ✨ FIX IS HERE: Add a defensive check.
    // If the data object itself is null/undefined, or if the specific view inside it doesn't exist,
    // we can't render the chart. Return null or a placeholder.
    if (!data || !data[distributionViews[type]]) {
        return (
            <div className={styles.chartCard}>
                <div className={styles.chartHeader}>
                    <h3>{title}</h3>
                </div>
                <div className={styles.noData}>Loading data...</div>
            </div>
        );
    }
    
    // Convert the data object to an array for the Pie chart
    const chartData = Object.entries(data[distributionViews[type]]).map(([name, value]) => ({ name, value }));

    return (
        <div className={styles.chartCard}>
            <div className={styles.chartHeader}>
                <h3>{title}</h3>
                <div className={styles.viewToggle}>
                    <button
                        className={`${styles.toggleButton} ${distributionViews[type] === 'quantity' ? styles.active : ''}`}
                        onClick={() => setDistributionViews(prev => ({ ...prev, [type]: 'quantity' }))}
                    >
                        Items
                    </button>
                    <button
                        className={`${styles.toggleButton} ${distributionViews[type] === 'products' ? styles.active : ''}`}
                        onClick={() => setDistributionViews(prev => ({ ...prev, [type]: 'products' }))}
                    >
                        Products
                    </button>
                </div>
            </div>
            {/* ✨ Add a check for empty data to show a message instead of an empty chart */}
            {chartData.length === 0 ? (
                <div className={styles.noData}>No data available for this view.</div>
            ) : (
                <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                        <Pie
                            data={chartData} // Use the processed array
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                        >
                            {chartData.map((_, index) => (
                                <Cell key={`cell-dist-${type}-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            )}
        </div>
    );
};

    const renderTopProductsChart = (data, title) => (
        <div className={styles.chartCard}>
            <div className={styles.chartHeader}>
                <h3>{title}</h3>
            </div>
            <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                    <Pie
                        data={data}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                    >
                        {data.map((entry, index) => (
                            <Cell key={`cell-top-product-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, name]} />
                    <Legend />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
    
// Dashboard.js

const breakdownToSortedArray = (breakdown, view = 'quantity') => {
    // ✨ FIX: Add a guard clause. If the breakdown or the specific view within it is missing, return an empty array.
    if (!breakdown || !breakdown[view]) {
        return [];
    }
    return Object.entries(breakdown[view])
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value);   // largest first
}

const renderCategoryBarChart = (breakdown, title) => {
    // ✨ FIX: Pass the correct view from the state for categories
    const data = breakdownToSortedArray(breakdown, distributionViews.category); 
    const maxVal = data.length > 0 ? Math.max(...data.map(d => d.value)) : 0;
    const chartHeight = Math.max(300, data.length * 24);

    return (
        <div className={styles.chartCard}>
        <div className={styles.chartHeader}>
            <h3>{title}</h3>
            <div className={styles.viewToggle}>
            {/* ✨ FIX: The toggle should affect 'category', not 'department' */}
            <button
                className={`${styles.toggleButton} ${distributionViews.category === 'quantity' ? styles.active : ''}`}
                onClick={() => setDistributionViews(prev => ({ ...prev, category: 'quantity' }))}
            >
                Items
            </button>
            <button
                className={`${styles.toggleButton} ${distributionViews.category === 'products' ? styles.active : ''}`}
                onClick={() => setDistributionViews(prev => ({ ...prev, category: 'products' }))}
            >
                Products
            </button>
            </div>
        </div>

        {/* ✨ FIX: Add a check for empty data to show a message */}
        {data.length === 0 ? (
            <div className={styles.noData}>No data available for this view.</div>
        ) : (
            <ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart
                    data={data}
                    layout="vertical"
                    margin={{ top: 0, right: 30, left: 100, bottom: 0 }}
                    barGap={4}
                >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis type="number" hide />
                    <YAxis
                        type="category"
                        dataKey="name"
                        width={180}
                        tick={{ fontSize: 12, width: 170 }} // Add width to tick to help with wrapping
                        interval={0} // Ensure all labels are shown
                    />
                    <Tooltip />
                    <Bar
                        dataKey="value"
                        isAnimationActive={true}
                        radius={[0, 6, 6, 0]}
                    >
                        <LabelList dataKey="value" position="right" style={{ fill: '#333' }} />
                        {data.map((entry, idx) => (
                            <Cell
                                key={`cell-${idx}`}
                                fill={entry.value === maxVal ? '#FF8042' : `hsl(${BAR_BASE_HUE + idx * BAR_HUE_STEP}, 70%, 55%)`}
                            />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        )}
        </div>
    );
};
    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Dashboard</h1>
            </div>
            <div className={styles.quickStats}>
                <div className={`${styles.statCard} ${styles.filtered}`}>
                    <div className={styles.cardHeader}>
                        <h3>Orders Overview</h3>
                        <select 
                            value={orderOverviewTimeRange}
                            onChange={(e) => setOrderOverviewTimeRange(e.target.value)}
                            className={styles.select}
                        >
                            <option value="1">Last Month</option>
                            <option value="3">Last 3 Months</option>
                            <option value="6">Last 6 Months</option>
                            <option value="12">Last 12 Months</option>
                            <option value="all">All Time</option>
                        </select>
                    </div>
                    <div className={styles.statGrid}>
                        <div className={`${styles.statItem} ${styles.totalOrders}`}>
                            <span>Total Orders</span>
                            <strong>{statistics.orders.total}</strong>
                        </div>
                        <div className={`${styles.statItem} ${styles.committed}`}>
                            <span>Committed</span>
                            <strong>{statistics.orders.committed}</strong>
                        </div>
                        <div className={`${styles.statItem} ${styles.issued}`}>
                            <span>Issued</span>
                            <strong>{statistics.orders.issued}</strong>
                        </div>
                        <div className={`${styles.statItem} ${styles.returned}`}>
                            <span>Returns</span>
                            <strong>{statistics.orders.returned}</strong>
                        </div>
                        <div className={`${styles.statItem} ${styles.averageValue}`}>
                            <span>Average Value</span>
                            <strong>${statistics.orders.averageValue.toFixed(2)}</strong>
                        </div>
                    </div>
                </div>
                <div className={`${styles.statCard} ${styles.filtered}`}>
                    <h3>Partial Returns & Cancellations</h3>
                    <div className={styles.timeRangeIndicator}>
                        Showing data for {orderOverviewTimeRange === 'all' ? 'All Time' : `Last ${orderOverviewTimeRange} Month${orderOverviewTimeRange === '1' ? '' : 's'}`}
                    </div>
                    <div className={styles.statGrid}>
                        <div className={`${styles.statItem} ${styles.partiallyReturned}`}>
                            <span>Partial Returns</span>
                            <strong>{statistics.orders.partially_returned}</strong>
                        </div>
                        <div className={`${styles.statItem} ${styles.returnRate}`}>
                            <span>Partial Return Rate</span>
                            <strong>{statistics.orders.partialReturnRate.toFixed(1)}%</strong>
                        </div>
                        <div className={`${styles.statItem} ${styles.cancelled}`}>
                            <span>Cancelled</span>
                            <strong>{statistics.orders.cancelled}</strong>
                        </div>
                        <div className={`${styles.statItem} ${styles.cancellationRate}`}>
                            <span>Cancellation Rate</span>
                            <strong>{statistics.orders.cancellationRate.toFixed(1)}%</strong>
                        </div>
                    </div>
                </div>

                <div className={styles.statCard}>
                    <h3>Inventory Status</h3>
                    <div className={styles.statGrid}>
                    <div className={`${styles.statItem} ${styles.totalProducts}`}>
                        <span>Total Products</span>
                        <strong>{statistics.products.total}</strong>
                    </div>
                    <div className={`${styles.statItem} ${styles.totalItems}`}>
                        <span>Total Items</span>
                        <strong>{statistics.products.totalItems}</strong>
                    </div>
                    <div className={`${styles.statItem} ${styles.availableProducts}`}>
                        <span>Available Products</span>
                        <strong>{statistics.products.availableProducts}</strong>
                    </div>
                    <div className={`${styles.statItem} ${styles.availableItems}`}>
                        <span>Available Items</span>
                        <strong>{statistics.products.availableItems}</strong>
                    </div>
                    </div>
                </div>

                <div className={styles.statCard}>
                    <h3>Users Stats</h3>
                    <div className={styles.statGrid}>
                        <div className={`${styles.statItem} ${styles.totalUsers}`}>
                            <span>Total Users</span>
                            <strong>{statistics.users.total}</strong>
                        </div>
                        <div className={`${styles.statItem} ${styles.adminUsers}`}>
                            <span>Admin Users</span>
                            <strong>{statistics.users.adminUsers}</strong>
                        </div>
                        <div className={`${styles.statItem} ${styles.regularUsers}`}>
                            <span>Regular Users</span>
                            <strong>{statistics.users.regularUsers}</strong>
                        </div>
                            <div className={`${styles.statItem} ${styles.activeUsers}`}>
                                <span>Active Users</span>
                                <strong>{statistics.users.active}</strong>
                            </div>
                    </div>
                </div>
            </div>

            <div className={styles.chartsGrid}>
                <div className={styles.chartCard}>
                    <div className={styles.cardHeader}>
                        <h3>Order Trends</h3>
                        <select 
                            value={orderTrendsTimeRange}
                            onChange={(e) => setOrderTrendsTimeRange(e.target.value)}
                            className={styles.select}
                        >
                            <option value="3">Last 3 Months</option>
                            <option value="6">Last 6 Months</option>
                            <option value="12">Last 12 Months</option>
                        </select>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={statistics.orderTrends}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Area 
                                type="monotone"
                                dataKey="orders"
                                stroke="#8884d8"
                                fill="#8884d8"
                                name="Orders"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
                <EditTrackedProductsModal
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    allProducts={allProducts}
                    currentTrackedIds={trackedProductIds}
                    onSave={handleSaveTopProducts} // Directly set the new array of IDs
            />
            
                {renderPieChart(statistics.products.warehouseBreakdown, 'Product Distribution by Warehouse', 'warehouse')}
                {renderPieChart(statistics.products.departmentBreakdown, 'Product Distribution by Department', 'department')}
                {renderCategoryBarChart(statistics.products.categoryBreakdown, 'Product Distribution by Category')}
                {renderTrackedProductsChart(statistics.products.trackedProductsData, 'Products Snap')}

            </div>
        </div>
    );
};

export default Dashboard;